import { useCallback, useMemo, useState } from 'react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import type {
  PendingLineItemChange,
  PendingHeaderChange,
} from 'src/types/application/SalesOrderDraftDTO';
import useReviewPendingChanges from 'src/api/accounts-receivable/sales-order-drafts/useReviewPendingChanges';
import { T, useGT } from 'gt-react';

interface Props {
  salesOrderDraftId: string;
  pendingLineItemChanges: PendingLineItemChange[];
  pendingHeaderChanges: PendingHeaderChange[];
  originalTotalAmount: number | null;
  onReviewComplete: () => void;
}

export type Decision = 'approved' | 'denied';

function isAddressLike(value: unknown): value is {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
} {
  return typeof value === 'object' && value !== null && 'street' in value;
}

const CURRENCY_FIELDS = new Set(['unitPrice', 'amount']);

function formatValue(
  value: unknown,
  field: string | undefined,
  naLabel: string,
): string {
  if (value == null) return naLabel;
  if (isAddressLike(value)) {
    return [value.street, value.city, value.state, value.zipCode]
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  if (typeof value === 'number') {
    if (field && CURRENCY_FIELDS.has(field))
      return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return String(value);
  }
  return String(value);
}

function formatFieldLabel(key: string, gt: ReturnType<typeof useGT>): string {
  switch (key) {
    case 'quantity':
      return gt('Qty', {
        context: 'sales order line field abbreviation quantity',
      });
    case 'unitPrice':
      return gt('Price', {
        context: 'sales order line field label unit price',
      });
    case 'amount':
      return gt('Amount', { context: 'sales order line field label amount' });
    case 'customerDescription':
      return gt('Description', {
        context: 'sales order line field label description',
      });
    default:
      return key;
  }
}

export function PendingChangesPanel({
  salesOrderDraftId,
  pendingLineItemChanges,
  pendingHeaderChanges,
  onReviewComplete,
}: Props) {
  const gt = useGT();
  const naLabel = gt('N/A', { context: 'missing value placeholder' });
  const [lineDecisions, setLineDecisions] = useState<Map<number, Decision>>(
    new Map(),
  );
  const [headerDecisions, setHeaderDecisions] = useState<Map<number, Decision>>(
    new Map(),
  );

  const reviewMutation = useReviewPendingChanges(onReviewComplete);

  const totalChanges =
    pendingLineItemChanges.length + pendingHeaderChanges.length;
  const totalDecided = lineDecisions.size + headerDecisions.size;
  const allDecided = totalDecided === totalChanges;

  const onLineDecision = useCallback(
    (index: number, decision: Decision) =>
      setLineDecisions((prev) => new Map(prev).set(index, decision)),
    [],
  );
  const onHeaderDecision = useCallback(
    (index: number, decision: Decision) =>
      setHeaderDecisions((prev) => new Map(prev).set(index, decision)),
    [],
  );
  const onAcceptAll = useCallback(() => {
    setLineDecisions(
      new Map(pendingLineItemChanges.map((_, i) => [i, 'approved' as const])),
    );
    setHeaderDecisions(
      new Map(pendingHeaderChanges.map((_, i) => [i, 'approved' as const])),
    );
  }, [pendingLineItemChanges, pendingHeaderChanges]);
  const onRejectAll = useCallback(() => {
    setLineDecisions(
      new Map(pendingLineItemChanges.map((_, i) => [i, 'denied' as const])),
    );
    setHeaderDecisions(
      new Map(pendingHeaderChanges.map((_, i) => [i, 'denied' as const])),
    );
  }, [pendingLineItemChanges, pendingHeaderChanges]);

  const handleSubmit = useCallback(() => {
    reviewMutation.mutate({
      stagedPurchaseOrderId: salesOrderDraftId,
      lineItemDecisions: pendingLineItemChanges.map((_, i) => ({
        index: i,
        decision: lineDecisions.get(i) ?? 'denied',
      })),
      headerDecisions: pendingHeaderChanges.map((_, i) => ({
        index: i,
        decision: headerDecisions.get(i) ?? 'denied',
      })),
    });
  }, [
    reviewMutation,
    salesOrderDraftId,
    pendingLineItemChanges,
    pendingHeaderChanges,
    lineDecisions,
    headerDecisions,
  ]);

  const lineItemsByType = useMemo(() => {
    const updates: Array<{ change: PendingLineItemChange; idx: number }> = [];
    const adds: Array<{ change: PendingLineItemChange; idx: number }> = [];
    const deletes: Array<{ change: PendingLineItemChange; idx: number }> = [];
    pendingLineItemChanges.forEach((change, idx) => {
      if (change.changeType === 'update') updates.push({ change, idx });
      else if (change.changeType === 'add') adds.push({ change, idx });
      else if (change.changeType === 'delete') deletes.push({ change, idx });
    });
    return { updates, adds, deletes };
  }, [pendingLineItemChanges]);

  return (
    <MDBox>
      {/* Header */}
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <MDTypography variant="h6" sx={{ fontSize: '14px' }}>
          <T context="EDI 860 change request panel title">860 Change Request</T>
        </MDTypography>
        <MDBox display="flex" gap={1}>
          <button
            type="button"
            onClick={onAcceptAll}
            className="text-xs text-green-600 hover:underline font-medium"
          >
            <T context="accept all pending sales order changes">Accept all</T>
          </button>
          <button
            type="button"
            onClick={onRejectAll}
            className="text-xs text-red-600 hover:underline font-medium"
          >
            <T context="reject all pending sales order changes">Reject all</T>
          </button>
        </MDBox>
      </MDBox>

      {/* Header changes */}
      {pendingHeaderChanges.map((change, idx) => {
        const decision = headerDecisions.get(idx) ?? null;
        return (
          <ChangeItem
            key={`h-${idx}`}
            decision={decision}
            onAccept={() => onHeaderDecision(idx, 'approved')}
            onReject={() => onHeaderDecision(idx, 'denied')}
            label={change.label}
          >
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-400 line-through">
                {formatValue(change.originalValue, change.field, naLabel)}
              </span>
              <span className="text-gray-300">→</span>
              <span className="font-medium">
                {formatValue(change.proposedValue, change.field, naLabel)}
              </span>
            </div>
          </ChangeItem>
        );
      })}

      {/* Line item updates */}
      {lineItemsByType.updates.map(({ change, idx }) => {
        const desc =
          (typeof change.originalValues?.customerDescription === 'string'
            ? change.originalValues.customerDescription
            : undefined) ??
          gt('Line {n}', {
            context: 'fallback label for sales order line by index',
            n: String(change.originalLineItemIndex + 1),
          });
        const decision = lineDecisions.get(idx) ?? null;
        return (
          <ChangeItem
            key={`u-${idx}`}
            decision={decision}
            onAccept={() => onLineDecision(idx, 'approved')}
            onReject={() => onLineDecision(idx, 'denied')}
            label={desc}
            badge={gt('Update', {
              context: 'badge for line item update in 860 change',
            })}
            badgeColor="#2563eb"
          >
            {Object.entries(change.proposedValues).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2 text-sm">
                <span className="text-gray-400 w-16 text-xs">
                  {formatFieldLabel(key, gt)}
                </span>
                <span className="text-gray-400 line-through">
                  {formatValue(change.originalValues?.[key], key, naLabel)}
                </span>
                <span className="text-gray-300">→</span>
                <span className="font-medium">
                  {formatValue(value, key, naLabel)}
                </span>
              </div>
            ))}
          </ChangeItem>
        );
      })}

      {/* Line item adds */}
      {lineItemsByType.adds.map(({ change, idx }) => {
        const desc =
          (typeof change.proposedValues.customerDescription === 'string'
            ? change.proposedValues.customerDescription
            : undefined) ??
          gt('New Item', {
            context: 'fallback label for new line item in 860 change',
          });
        const decision = lineDecisions.get(idx) ?? null;
        return (
          <ChangeItem
            key={`a-${idx}`}
            decision={decision}
            onAccept={() => onLineDecision(idx, 'approved')}
            onReject={() => onLineDecision(idx, 'denied')}
            label={desc}
            badge={gt('Add', {
              context: 'badge for new line item in 860 change',
            })}
            badgeColor="#16a34a"
          >
            {change.proposedValues.quantity != null && (
              <div className="text-sm text-gray-600">
                {gt('Qty: {qty}', {
                  context: 'quantity label for new line',
                  qty: String(change.proposedValues.quantity),
                })}
              </div>
            )}
            {change.proposedValues.unitPrice != null && (
              <div className="text-sm text-gray-600">
                {gt('Price: ${price}', {
                  context: 'unit price for new line item',
                  price: Number(change.proposedValues.unitPrice).toFixed(2),
                })}
              </div>
            )}
          </ChangeItem>
        );
      })}

      {/* Line item deletes */}
      {lineItemsByType.deletes.map(({ change, idx }) => {
        const desc =
          (typeof change.originalValues?.customerDescription === 'string'
            ? change.originalValues.customerDescription
            : undefined) ??
          gt('Line {n}', {
            context: 'fallback label for sales order line by index',
            n: String(change.originalLineItemIndex + 1),
          });
        const decision = lineDecisions.get(idx) ?? null;
        return (
          <ChangeItem
            key={`d-${idx}`}
            decision={decision}
            onAccept={() => onLineDecision(idx, 'approved')}
            onReject={() => onLineDecision(idx, 'denied')}
            label={desc}
            badge={gt('Delete', {
              context: 'badge for deleted line item in 860 change',
            })}
            badgeColor="#dc2626"
          >
            <div className="text-sm text-gray-500">
              {gt('Qty: {qty} · Price: {price}', {
                context: 'summary for deleted line qty and price',
                qty: String(change.originalValues?.quantity ?? naLabel),
                price: formatValue(
                  change.originalValues?.unitPrice,
                  'unitPrice',
                  naLabel,
                ),
              })}
            </div>
          </ChangeItem>
        );
      })}

      {/* Progress + Confirm */}
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1.5}
      >
        <MDTypography variant="caption" sx={{ color: '#9ca3af' }}>
          {gt('{done}/{total} reviewed', {
            context: 'progress of reviewed pending changes',
            done: String(totalDecided),
            total: String(totalChanges),
          })}
        </MDTypography>
        <button
          type="button"
          disabled={!allDecided || reviewMutation.isPending}
          onClick={handleSubmit}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            allDecided && !reviewMutation.isPending
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {reviewMutation.isPending ? (
            <T context="applying reviewed 860 changes">Applying...</T>
          ) : (
            <T context="confirm reviewed 860 changes">Confirm Changes</T>
          )}
        </button>
      </MDBox>
    </MDBox>
  );
}

/** A single change action item with accept/reject buttons */
function ChangeItem({
  decision,
  onAccept,
  onReject,
  label,
  badge,
  badgeColor,
  children,
}: {
  decision: Decision | null;
  onAccept: () => void;
  onReject: () => void;
  label: string;
  badge?: string;
  badgeColor?: string;
  children: React.ReactNode;
}) {
  const gt = useGT();
  return (
    <div
      className={`mb-2 rounded-lg border p-3 transition-all ${
        decision === 'approved'
          ? 'border-green-200 bg-green-50/40'
          : decision === 'denied'
            ? 'border-gray-200 bg-gray-50 opacity-60'
            : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {badge && (
              <span
                className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded"
                style={{
                  color: badgeColor,
                  backgroundColor: `${badgeColor}15`,
                }}
              >
                {badge}
              </span>
            )}
            <span className="text-sm font-medium text-gray-700 truncate">
              {label}
            </span>
          </div>
          <div
            className={
              decision === 'denied' ? 'opacity-40 line-through' : undefined
            }
          >
            {children}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
          <button
            type="button"
            onClick={onAccept}
            aria-label={gt('Accept change', {
              context: 'aria label accept pending change',
            })}
            aria-pressed={decision === 'approved'}
            className={`p-1 rounded-full transition-colors ${
              decision === 'approved'
                ? 'text-green-600 bg-green-100'
                : 'text-gray-300 hover:text-green-500 hover:bg-green-50'
            }`}
          >
            <CheckCircleIcon
              className="h-5 w-5"
              weight={decision === 'approved' ? 'fill' : 'regular'}
            />
          </button>
          <button
            type="button"
            onClick={onReject}
            aria-label={gt('Reject change', {
              context: 'aria label reject pending change',
            })}
            aria-pressed={decision === 'denied'}
            className={`p-1 rounded-full transition-colors ${
              decision === 'denied'
                ? 'text-red-600 bg-red-100'
                : 'text-gray-300 hover:text-red-500 hover:bg-red-50'
            }`}
          >
            <XCircleIcon
              className="h-5 w-5"
              weight={decision === 'denied' ? 'fill' : 'regular'}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PendingChangesPanel;
